import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { useReorderSensors } from "@/hooks/useReorderSensors";
import { reorderByDragEnd } from "@/lib/reorder";
import { Plus, Tags } from "lucide-react";
import { toast } from "react-hot-toast";
import {
  TranslationInput,
  SectionCard,
  CatalogTranslatePanel,
} from "@/components/common";
import {
  Button,
  ConfirmModal,
  Modal,
  ModalFooter,
  EmptyState,
} from "@/components/ui";
import { activityService } from "@/services/activityService";
import { toastApiError } from "@/lib/errorMessages";
import {
  applyAutoTranslateResult,
  useCatalogAutoTranslate,
} from "@/hooks/useCatalogAutoTranslate";
import {
  buildRequiredTranslations,
  findAutoTranslateSourceLanguage,
  findMissingTranslations,
  resolveCatalogTranslation,
  type TranslationFormValues,
} from "@/lib/translationResolution";
import { resolveLocale } from "@/lib/locales";
import type { Activity, ServiceCategory } from "@/types";
import { SortableServiceCategoryItem } from "./SortableServiceCategoryItem";

interface ServiceCategoriesSectionProps {
  activityId: string;
  categories: ServiceCategory[];
  /** Restaurants keep hardcoded menu courses; extras are additive. */
  includeDefaultMenuCourses?: boolean;
}

function sortCategoriesByDisplayOrder(
  categories: ServiceCategory[],
): ServiceCategory[] {
  return [...categories].sort((a, b) => a.display_order - b.display_order);
}

/** Assign display_order from current array position — do not sort first. */
function reindexCategoriesByArrayOrder(
  categories: ServiceCategory[],
): ServiceCategory[] {
  return categories.map((category, index) => ({
    ...category,
    display_order: index,
  }));
}

function normalizeCategories(categories: ServiceCategory[]): ServiceCategory[] {
  return reindexCategoriesByArrayOrder(
    sortCategoriesByDisplayOrder(categories),
  );
}

export function ServiceCategoriesSection({
  activityId,
  categories,
  includeDefaultMenuCourses = false,
}: ServiceCategoriesSectionProps) {
  const { t: tCommon } = useTranslation("common");
  const { t, i18n } = useTranslation("activities");
  const queryClient = useQueryClient();
  const sortedCategories = useMemo(
    () => normalizeCategories(categories),
    [categories],
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCategoryId, setEditingCategoryId] = useState<string | null>(
    null,
  );
  const [pendingDelete, setPendingDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [nameTranslations, setNameTranslations] =
    useState<TranslationFormValues>({});
  const [error, setError] = useState("");

  const preferredSourceLanguage = resolveLocale(i18n.language);
  const autoTranslateSourceLanguage =
    findAutoTranslateSourceLanguage(
      nameTranslations,
      preferredSourceLanguage,
    ) ?? preferredSourceLanguage;

  const autoTranslate = useCatalogAutoTranslate({
    activityId,
    sourceLanguage: autoTranslateSourceLanguage,
    nameTranslations,
    descriptionTranslations: {},
    requireDescription: false,
    onApplied: (result) => {
      const applied = applyAutoTranslateResult(nameTranslations, {}, result, {
        mergeDescriptions: false,
      });
      setNameTranslations(applied.nameTranslations);
      if (error) {
        setError("");
      }
    },
    onMissingName: () => setError(t("serviceCategories.fillOneLanguageFirst")),
  });

  const sensors = useReorderSensors();

  const applyOptimisticCategories = (nextCategories: ServiceCategory[]) => {
    queryClient.setQueryData<Activity>(["activity", activityId], (old) => {
      if (!old) return old;
      return {
        ...old,
        service_categories: reindexCategoriesByArrayOrder(nextCategories),
      };
    });
  };

  const saveMutation = useMutation({
    mutationFn: (nextCategories: ServiceCategory[]) =>
      activityService.update(activityId, {
        service_categories: normalizeCategories(nextCategories),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activity", activityId] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["serviceItems", activityId] });
      toast.success(t("detail.serviceCategoriesUpdated"));
      handleClose();
      setPendingDelete(null);
    },
    onError: (err) => {
      toastApiError(err, "updateServiceCategories");
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (nextCategories: ServiceCategory[]) =>
      activityService.update(activityId, {
        service_categories: reindexCategoriesByArrayOrder(nextCategories),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["activity", activityId] });
      queryClient.invalidateQueries({ queryKey: ["activities"] });
      queryClient.invalidateQueries({ queryKey: ["serviceItems", activityId] });
    },
    onError: (err) => {
      toastApiError(err, "updateServiceCategories");
      queryClient.invalidateQueries({ queryKey: ["activity", activityId] });
    },
  });

  const isBusy =
    saveMutation.isPending ||
    reorderMutation.isPending ||
    autoTranslate.isPending;

  const handleClose = () => {
    setIsModalOpen(false);
    setEditingCategoryId(null);
    setNameTranslations({});
    setError("");
  };

  const handleAdd = () => {
    setEditingCategoryId(null);
    setNameTranslations({});
    setError("");
    setIsModalOpen(true);
  };

  const handleEdit = (category: ServiceCategory) => {
    setEditingCategoryId(category.id);
    setNameTranslations({ ...category.name_translations });
    setError("");
    setIsModalOpen(true);
  };

  const handleSubmit = () => {
    if (findMissingTranslations(nameTranslations).length > 0) {
      setError(t("serviceCategories.allTranslationsRequired"));
      return;
    }

    const namePayload = buildRequiredTranslations(nameTranslations);

    const nextCategory: ServiceCategory = {
      id: editingCategoryId || crypto.randomUUID(),
      name_translations: namePayload,
      display_order:
        sortedCategories.find((category) => category.id === editingCategoryId)
          ?.display_order ?? sortedCategories.length,
    };

    const nextCategories = editingCategoryId
      ? sortedCategories.map((category) =>
          category.id === editingCategoryId ? nextCategory : category,
        )
      : [...sortedCategories, nextCategory];

    saveMutation.mutate(nextCategories);
  };

  const handleDelete = (categoryId: string) => {
    const category = sortedCategories.find((item) => item.id === categoryId);
    const name = category
      ? resolveCatalogTranslation(
          category.name_translations,
          i18n.language,
          t("serviceCategories.thisCategory"),
        )
      : t("serviceCategories.thisCategory");
    setPendingDelete({ id: categoryId, name });
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const reordered = reorderByDragEnd(
      sortedCategories,
      event,
      (category) => category.id,
    );
    if (!reordered) return;

    applyOptimisticCategories(reordered);
    reorderMutation.mutate(reordered);
  };

  return (
    <>
      <SectionCard
        title={t("serviceCategories.title")}
        icon={Tags}
        count={sortedCategories.length}
        contentClassName="space-y-0"
        action={
          <Button
            type="button"
            size="sm"
            variant="primary"
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={handleAdd}
          >
            {t("serviceCategories.addCategory")}
          </Button>
        }
      >
        {sortedCategories.length === 0 ? (
          <EmptyState
            variant="dashed"
            size="sm"
            icon={Tags}
            title={
              includeDefaultMenuCourses
                ? t("serviceCategories.emptyTitleMenu")
                : t("serviceCategories.emptyTitle")
            }
            description={
              includeDefaultMenuCourses
                ? t("serviceCategories.emptyDescriptionMenu")
                : t("serviceCategories.emptyDescription")
            }
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortedCategories.map((category) => category.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {includeDefaultMenuCourses && (
                  <p className="text-surface-400 text-xs">
                    {t("serviceCategories.standardCoursesHint")}
                  </p>
                )}
                {sortedCategories.map((category, index) => (
                  <SortableServiceCategoryItem
                    key={category.id}
                    category={category}
                    orderIndex={index + 1}
                    onEdit={() => handleEdit(category)}
                    onDelete={() => handleDelete(category.id)}
                    disabled={isBusy}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </SectionCard>

      <Modal
        closeLabel={tCommon("close")}
        isOpen={isModalOpen}
        onClose={handleClose}
        title={
          editingCategoryId
            ? t("serviceCategories.modalEditTitle")
            : t("serviceCategories.modalAddTitle")
        }
        size="lg"
        footer={
          <ModalFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
            >
              {tCommon("cancel")}
            </Button>
            <Button
              type="button"
              variant="primary"
              onClick={handleSubmit}
              isLoading={saveMutation.isPending}
            >
              {editingCategoryId
                ? t("serviceCategories.saveChanges")
                : t("serviceCategories.addCategory")}
            </Button>
          </ModalFooter>
        }
      >
        <div className="space-y-4">
          <CatalogTranslatePanel
            hint={t("serviceCategories.autoTranslateHint")}
            buttonLabel={t("serviceCategories.autoTranslateGenerate")}
            onTranslate={autoTranslate.run}
            isLoading={autoTranslate.isPending}
            disabled={saveMutation.isPending || autoTranslate.isPending}
          />
          <TranslationInput
            label={t("serviceCategories.nameLabel")}
            values={nameTranslations}
            onChange={setNameTranslations}
            placeholder={t("serviceCategories.namePlaceholder")}
            required
            error={error || undefined}
          />
        </div>{" "}
      </Modal>

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}
        confirmText={tCommon("confirm")}
        isOpen={autoTranslate.confirmOpen}
        onClose={autoTranslate.onConfirmClose}
        onConfirm={autoTranslate.onConfirm}
        title={autoTranslate.confirmTitle}
        message={autoTranslate.confirmMessage}
        isLoading={autoTranslate.isPending}
      />

      <ConfirmModal
        closeLabel={tCommon("close")}
        cancelText={tCommon("cancel")}

        isOpen={pendingDelete != null}
        onClose={() => {
          if (saveMutation.isPending) return;
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (!pendingDelete) return;
          saveMutation.mutate(
            sortedCategories.filter((item) => item.id !== pendingDelete.id),
          );
        }}
        title={t("serviceCategories.delete")}
        message={t("serviceCategories.deleteConfirm", {
          name: pendingDelete?.name ?? t("serviceCategories.thisCategory"),
        })}
        confirmText={t("serviceCategories.delete")}
        isDestructive
        isLoading={saveMutation.isPending}
      />
    </>
  );
}
